import { VAlertComponent } from "quickfire/components/v-alert-component";
import QuickfireControlList from "quickfire/controls/quickfire-control-list";
import QuickfireMuteControl from "quickfire/controls/quickfire-mute-control";
import QuickfireStakeUpdateControl from "quickfire/controls/quickfire-stake-update-control";
import recoveryRecordBuilder from "quickfire/integration/builders/record/recovery-record-builder";
import refreshRecordBuilder from "quickfire/integration/builders/record/refresh-record-builder";
import spinRecordBuilder from "quickfire/integration/builders/record/spin-record-builder";
import freespinRequestBuilder from "quickfire/integration/builders/request/freespin-request-builder";
import refreshRequestBuilder from "quickfire/integration/builders/request/refresh-request-builder";
import spinRequestBuilder from "quickfire/integration/builders/request/spin-request-builder";
import lineResultBuilder from "quickfire/integration/builders/result/line-result-builder";
import scatterResultBuilder from "quickfire/integration/builders/result/scatter-result-builder";
import waysResultBuilder from "quickfire/integration/builders/result/ways-result-builder";
import quickfireVConnector from "quickfire/integration/connector/quickfire-v-connector";
import qfSignals from "quickfire/qf-signals";
import { VAutoplay } from "quickfire/services/v-autoplay";
import { VCurrency } from "quickfire/services/v-currency";
import { VStorage } from "quickfire/services/v-storage";
import { VTransaction } from "quickfire/services/v-transaction";
import QuickfireIsRecoveryFreespinsSummary from "quickfire/states/quickfire-is-recovery-freespins-summary";
import QuickfireRecoverStake from "quickfire/states/quickfire-recover-stake";
import { VEventEnum } from "quickfire/v/enums";
import Components from "slotworks/components/components";
import ReelSpinner from "slotworks/components/matrix/reel/transition-behaviours/spin/reel-spinner";
import SpinReelTransition from "slotworks/components/matrix/reel/transition-behaviours/spin/spin-reel-transition";
import canvasManager from "slotworks/graphics/canvas/canvas-manager";
import CanvasManager from "slotworks/graphics/canvas/canvas-manager";
import Layers from "slotworks/graphics/layers/layers";
import Loader from "slotworks/loader/loader";
import AutoplayModel from "slotworks/model/autoplay/autoplay-model";
import BetModel from "slotworks/model/bets/bet-model";
import regulationsModel from "slotworks/model/regulations/regulations-model";
import SettingsModel from "slotworks/model/settings/settings-model";
import builderManager from "slotworks/server/builders/builder-manager";
import commsManager from "slotworks/server/comms-manager";
import { Services } from "slotworks/services/services";
import SmartUIFlags from "slotworks/smart-ui/smart-ui-flags";
import { SmartUIFlag } from "slotworks/smart-ui/smart-ui-flags";
import SmartUIManager from "slotworks/smart-ui/smart-ui-manager";
import StateMachine from "slotworks/state-machine/state-machine";
import StateRegister from "slotworks/state-machine/state-register";
import Contract from "slotworks/utils/contracts/contract";
import { parallel } from "slotworks/utils/race-utils";
import Timer from "slotworks/utils/timer";
import gameLoop from 'slotworks/core/game-loop';

class Quickfire {

    public paytable: Map<string, QuickfirePaytablePayout> = new Map<string, QuickfirePaytablePayout>();
    public symbolNameMap: Map<string, string> = new Map<string, string>();

    public menuExclusions: string[] = ["SmartButton"];

    private menuVisible: boolean;

    /**
     * Setup QuickFire V
     *
     * Example usage:
     *
     * SetupQuickfire(
     *     () => {
     *         configure();
     *         setupComponents();
     *         setupComms();
     *         setupStates();
     *     },
     *     (callback) => {
     *         SlotWorks.go(gameConfig, stateConfig, assetConfig, componentConfig, soundConfig, i18nConfig, callback);
     *     },
     *     () => ready()
     * );
     *
     * @param init  Call before all setup
     * @param start Start slotworks (and call callback when ready)
     * @param ready When QF preloader is complete and game is ready to start
     */
    public start(init: () => void, start: (callback: Function) => void, ready: () => void) {
        CanvasManager.iosScrollPromptEnabled = false;

        BetModel.saveBetOptions = false;

        Services.StorageType = VStorage;
        Services.TransactionType = VTransaction;
        Services.CurrencyType = VCurrency;
        const autoplayConfig = V.API.Autoplay.getConfig();
        if (autoplayConfig && autoplayConfig.useWagerLimitApi) {
            Services.AutoplayType = VAutoplay;
        }

        SettingsModel.language = V.API.Core.getCasinoState().lang;

        Components.AlertComponentType = VAlertComponent;

        this.setupComms();

        (window as any).initGame = (config: any, assets: any, deviceProps: any, callback: any, automation: any, callbackObj: any, gameSettingsConfig: any) => {
            init();

            this.setupStates();

            commsManager.connector.onError((message) => V.API.Dialog.showErrorDialog(message));

            canvasManager.rootNode = document.getElementById("main");

            Loader.setBaseURL(V.API.UrlHelper.getGameUrl());

            qfSignals.init();
            qfSignals.get(VEventEnum.preloaderRemoved).addOnce(() => {
                ready();
                this.fixVideoSourcePaths();
            });

            start(() => {
                this.setupAutoplay();
                this.setupSignals();
                this.initialSignals();
                this.processGameSettingsConfig(gameSettingsConfig);
                this.fixCSSPaths().then(() => callback.call(callbackObj));
                this.setupVScrollCSSPoll();
            });

            this.setupControls();

            Components.preloader.onProgress.add((progress: number) => V.API.Core.updateProgressBar(progress));
        };
    }

    private setupStates() {
        StateRegister.add("quickfire-is-recovery-freespins-summary-check", new QuickfireIsRecoveryFreespinsSummary());
        StateRegister.add("quickfire-recover-stake", new QuickfireRecoverStake(StateMachine));
    }

    private setupComms() {
        commsManager.connector = quickfireVConnector;

        builderManager.addRequestBuilder(refreshRequestBuilder);
        builderManager.addRequestBuilder(spinRequestBuilder);
        builderManager.addRequestBuilder(freespinRequestBuilder);

        builderManager.addRecordBuilder(refreshRecordBuilder);
        builderManager.addRecordBuilder(spinRecordBuilder);
        builderManager.addRecordBuilder(recoveryRecordBuilder);

        builderManager.addResultBuilder(lineResultBuilder);
        builderManager.addResultBuilder(scatterResultBuilder);
        builderManager.addResultBuilder(waysResultBuilder);
    }

    private setupControls() {
        QuickfireControlList.init();

        const qfGroup = SmartUIManager.newHeadlessSmartUIGroup("quickfire");
        qfGroup.addControl(new QuickfireStakeUpdateControl());
        qfGroup.addControl(new QuickfireMuteControl());

        const freeGamesGroup = SmartUIManager.newSmartUIGroup(Layers.Custom.get("QuickfireFreeGames"), "base");
        freeGamesGroup.addEnableCondition([SmartUIFlag.IDLE]);
        freeGamesGroup.addDisableCondition([SmartUIFlag.PROMPT]);
    }

    private setupSignals() {
        SmartUIManager.onGroupShow.add((name: string) => this.openMenu(name));
        SmartUIManager.onGroupHide.add((name: string) => this.closeMenu(name));
    }

    private openMenu(groupName: string) {
        if (!this.menuVisible && this.isGroupAMenu(groupName)) {
            qfSignals.dispatch(VEventEnum.gamemenuOpen);
            this.menuVisible = true;
        }
    }

    private closeMenu(groupName: string) {
        if (this.menuVisible && this.isGroupAMenu(groupName)) {
            qfSignals.dispatch(VEventEnum.gamemenuClosed);
            this.menuVisible = false;
        }
    }

    private isGroupAMenu(group: string) {
        for (const exclusion of this.menuExclusions) {
            if (group.indexOf(exclusion) > -1) {
                return false;
            }
        }

        return true;
    }

    private processGameSettingsConfig(gameSettingsConfig: any) {
        const certificationSettings = V.API.Core.processCertificationSettings(gameSettingsConfig);

        regulationsModel.turboAllowed = true;

        if (certificationSettings) {
            // Minimum spin times
            if (certificationSettings.spinDelay && certificationSettings.spinDelay > 0) {
                ReelSpinner.setMinSpinTime(certificationSettings.spinDelay);
            }

            // Disable quick spin
            if (!certificationSettings.quickSpins) {
                regulationsModel.turboAllowed = false;
            }
            if (certificationSettings.hideStop) {
                SpinReelTransition.allowSkips = false;
                regulationsModel.skipAllowed = false;

                StateMachine.onStateEnter.add((stateName: string) => {
                    if (stateName.indexOf("request-spin") > -1) {
                        SmartUIFlags.set(SmartUIFlag.NO_SKIP, true);
                    }
                });
            }
        }
    }

    private setupAutoplay() {
        const autoplayConfig = V.API.Autoplay.getConfig();

        AutoplayModel.stopOnFeatureWin = true;

        Services.autoplay.setEnabled(false);

        if (autoplayConfig) {
            if (autoplayConfig.spinCount !== undefined) {
                AutoplayModel.autoplays.values = autoplayConfig.spinCount;
                AutoplayModel.autoplays.defaultValue = AutoplayModel.autoplays.values[0];
            }

            if (autoplayConfig.lossLimitAmounts !== undefined) {
                regulationsModel.lossLimitRequired = true;
                AutoplayModel.lossLimit.values = this.parseLimits(autoplayConfig.lossLimitAmounts);
                AutoplayModel.lossLimit.defaultValue = AutoplayModel.lossLimit.values[0];
            }
            if (autoplayConfig.lossLimitDefault !== undefined) {
                AutoplayModel.lossLimit.defaultValue = this.convertToNumber(autoplayConfig.lossLimitDefault);
            }

            if (autoplayConfig.winLimitAmounts !== undefined) {
                AutoplayModel.singleWinLimit.values = this.parseLimits(autoplayConfig.winLimitAmounts);
                AutoplayModel.singleWinLimit.defaultValue = AutoplayModel.singleWinLimit.values[0];
            }
            if (autoplayConfig.winLimitDefault !== undefined) {
                AutoplayModel.singleWinLimit.defaultValue = this.convertToNumber(autoplayConfig.winLimitDefault);
            }

            // fixes in correct config where the default has been set to a value which isnt in the array
            if (AutoplayModel.autoplays.values.indexOf(AutoplayModel.autoplays.defaultValue) === -1) {
                AutoplayModel.autoplays.defaultValue = AutoplayModel.autoplays.values[0];
            }

            if (AutoplayModel.lossLimit.values.indexOf(AutoplayModel.lossLimit.defaultValue) === -1) {
                AutoplayModel.lossLimit.defaultValue = AutoplayModel.lossLimit.values[0];
            }

            if (AutoplayModel.singleWinLimit.values.indexOf(AutoplayModel.singleWinLimit.defaultValue) === -1) {
                AutoplayModel.singleWinLimit.defaultValue = AutoplayModel.singleWinLimit.values[0];
            }

            //
            AutoplayModel.autoplays.currentValue = AutoplayModel.autoplays.defaultValue;
            AutoplayModel.lossLimit.currentValue = AutoplayModel.lossLimit.defaultValue;
            AutoplayModel.singleWinLimit.currentValue = AutoplayModel.singleWinLimit.defaultValue;

            if (autoplayConfig.untilStop) {
                regulationsModel.unlimitedAutoplayAllowed = true;
                AutoplayModel.autoplays.values.push(Infinity);
            }

            if (autoplayConfig.enabled) {
                Services.autoplay.setEnabled(true);
            } else {
                Services.autoplay.setEnabled(false);
            }
        }
    }

    private initialSignals() {
        qfSignals.dispatch(VEventEnum.gameSetMute, Services.sound.getMuted());
        qfSignals.dispatch(VEventEnum.betSizeUpdate, BetModel.getTotalStake());

        SmartUIFlags.onFlagsUpdated.add(() => {
            if (SmartUIFlags.bitmask === SmartUIFlag.IDLE) {
                qfSignals.dispatch(VEventEnum.gameplayPause);
            }
        });

        qfSignals.get(VEventEnum.fullscreenOverlayShown).add(()=>{
            gameLoop.setPaused(true);
        });

        qfSignals.get(VEventEnum.fullscreenOverlayRemoved).add(()=>{
            gameLoop.setPaused(false);
        });
    }

    private parseLimits(values: string[]) {
        const mapped = values.map((value) => this.convertToNumber(value));
        mapped.shift();
        return mapped;
    }

    private convertToNumber(value: string) {
        return (value === null || value === undefined) ? 0 : (value === "None" ? 0 : parseFloat(value));
    }

    private fixVideoSourcePaths(): void {
        const baseURL: string = V.API.UrlHelper.getGameUrl();

        const videos: NodeListOf<HTMLVideoElement> = document.querySelectorAll("#main video");
        for (const videoElement of videos as any) {
            const idValue: string = videoElement.getAttribute("id");

            for (const extension of ["webm", "mp4"]) {
                const source = document.createElement("source");
                source.src = baseURL + "video/" + idValue + "@" + canvasManager.layoutScale + "x." + extension;
                source.type = "video/" + extension;
                videoElement.appendChild(source);
            }

            videoElement.load();
        }
    }

    private fixCSSPaths() {
        const imageLoads: Array<() => Contract<void>> = [];

        const baseURL = V.API.UrlHelper.getGameUrl();

        const readChildren = (element: HTMLElement) => {
            if (element.children) {
                for (const child of element.children as any) {
                    readChildren(child as HTMLElement);
                }
            }

            const backgroundImage = (window as any).getComputedStyle(element, null).getPropertyValue("background-image");
            if (backgroundImage) {
                if (backgroundImage.indexOf("img") > -1) {
                    const image = /(img\/.*?)\"?\)/g.exec(backgroundImage)[1];
                    const imageURL = `${baseURL}${image}`;
                    const backgroundImageStyle = `url("${imageURL}")`;

                    imageLoads.push(() => new Contract<void>((resolve) => {
                        const preloadImage = new Image();
                        preloadImage.onload = () => {
                            (element as any).style.backgroundImage = backgroundImageStyle;
                            resolve();
                        };

                        preloadImage.src = imageURL;
                    }));

                }
            }
        };

        readChildren(document.getElementById("main"));

        return parallel(imageLoads);
    }

    private setupVScrollCSSPoll() {
        Timer.setInterval(() => {
            try {
                canvasManager.rootNode.classList.toggle("vPromptVisible", V.API.Fullscreen && V.API.Fullscreen.isOverlayVisible());
            } catch (e) {
                canvasManager.rootNode.classList.toggle("vPromptVisible", false);
            }
        }, 0);
    }
}

export class QuickfirePaytablePayout {
    constructor(public symbolIndex: number, public reason: string, public payout: number, public freespins: boolean = false, public freespinsAwarded: number = 0) { }
}

export default new Quickfire();
